import React, { useEffect, useRef } from 'react';
import {
  Editor,
  RichUtils,
  getDefaultKeyBinding,
  EditorState,
  Modifier,
  SelectionState,
} from 'draft-js';
import {
  getCustomStyleMap,
  getSelectionCustomInlineStyle,
  getSelectedBlocksMetadata,
  getSelectedBlock,
} from 'draftjs-utils';
import { useDispatch, useSelector } from 'react-redux';
import { designActions } from '../../actions';
import PropTypes from 'prop-types';
import _ from 'lodash';
import './VTextEditor.css';

const VTextEditor = props => {
  const editor = useRef(null);
  const dispatch = useDispatch();
  const entity = useSelector(state => state.design.entitiesState[props.entityId]);
  const editorState = useSelector(state => state.design.editorsState[props.entityId]);
  const enabledEditorId = useSelector(state => state.design.enabledEditorId);
  const selectedEntity = useSelector(state => state.design.selectedEntity);
  const fontSize = useSelector(state => state.design.fontSize);
  const fontFamily = useSelector(state => state.design.fontFamily);
  const fontColor = useSelector(state => state.design.fontColor);
  const inlineStyles = useSelector(state => state.design.inlineStyles);
  const textBlockType = useSelector(state => state.design.textBlockType);
  const textAlignment = useSelector(state => state.design.textAlignment);
  const textTransform = useSelector(state => state.design.textTransform);

  useEffect(() => {
    if (props.entityId !== enabledEditorId) onBlur();
    else onChange(EditorState.moveFocusToEnd(editorState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledEditorId, props.entityId]);

  useEffect(() => {
    if (!selectedEntity || selectedEntity.id !== props.entityId) return;
    const styles = {};
    const inlineFontStyles = getSelectionCustomInlineStyle(editorState, [
      'FONTSIZE',
      'FONTFAMILY',
      'COLOR',
    ]);
    const currentFontSize = inlineFontStyles.FONTSIZE
      ? Number(inlineFontStyles.FONTSIZE.substring(9)) * 0.75
      : 30;
    const currentFontFamily = inlineFontStyles.FONTFAMILY
      ? inlineFontStyles.FONTFAMILY.substring(11)
      : undefined;
    const currentFontColor = inlineFontStyles.COLOR ? inlineFontStyles.COLOR.substring(6) : '#fff';
    const currentInlineStyles = editorState.getCurrentInlineStyle();
    const currentTextBlockType = getSelectedBlock(editorState).get('type');
    const currentTextAlignment = getSelectedBlocksMetadata(editorState).get('text-align');
    const currentTextTransform = getSelectedBlocksMetadata(editorState).get('text-transform');
    if (fontSize !== currentFontSize) styles.fontSize = currentFontSize;
    if (fontFamily !== currentFontFamily) styles.fontFamily = currentFontFamily;
    if (fontColor !== currentFontColor) styles.fontColor = currentFontColor;
    if (inlineStyles !== currentInlineStyles) styles.inlineStyles = currentInlineStyles;
    if (textBlockType !== currentTextBlockType) styles.textBlockType = currentTextBlockType;
    if (textAlignment !== currentTextAlignment) styles.textAlignment = currentTextAlignment;
    if (textTransform !== currentTextTransform) styles.textTransform = currentTextTransform;
    if (!_.isEmpty(styles)) dispatch(designActions.setCurrentTextStyle(styles));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState, selectedEntity]);

  useEffect(() => {
    if (!editorState) return;

    editorState
      .getCurrentContent()
      .getBlockMap()
      .forEach(block => {
        if (block.getType().includes('-list-item')) {
          let fontSize = block
            .getInlineStyleAt(1)
            .toList()
            .toJS()
            .find(value => value.includes('fontsize-'));
          let color = block
            .getInlineStyleAt(1)
            .toList()
            .toJS()
            .find(value => value.includes('color-'));

          fontSize = fontSize ? fontSize.split('-')[1] : '30';
          color = color ? color.split('-')[1].replace('#', '') : '000';
          dispatch(designActions.addListBlockStyleClass({ fontsize: fontSize, color }));
        }

        block.getData().forEach((value, key) => {
          if (key === 'letter-spacing') {
            dispatch(designActions.addSpacingStyleClass(value, 1.4));
          } else if (key === 'line-height') {
            dispatch(designActions.addSpacingStyleClass(0, value));
          }
        });
      });
  }, [dispatch, editorState]);

  const getBlockStyleFn = block => {
    let styleClasses = '';
    if (block.getType().includes('-list-item')) {
      let fontsize = block
        .getInlineStyleAt(1)
        .toList()
        .toJS()
        .find(value => value.includes('fontsize-'));
      let color = block
        .getInlineStyleAt(1)
        .toList()
        .toJS()
        .find(value => value.includes('color-'));
      fontsize = fontsize ? fontsize.split('-')[1] : '30';
      color = color ? color.split('-')[1].replace('#', '') : '000';
      styleClasses = `${styleClasses} listBlock-${fontsize.replace('.', '_')}-${color}`;
    }
    if (block.getData()) {
      block.getData().forEach((value, key) => {
        switch (key) {
          case 'text-align':
            styleClasses = `${styleClasses} rdw-${value}-aligned-block`;
            break;
          case 'text-transform':
            styleClasses = `${styleClasses} ${value}-transform-block`;
            break;
          case 'letter-spacing':
            styleClasses = `${styleClasses} letterSpacing_${value.toString().replace('.', '-')}`;
            break;
          case 'line-height':
            styleClasses = `${styleClasses} lineHeight_${value.toString().replace('.', '-')}`;
            break;
          default:
            break;
        }
      });
    }
    return styleClasses;
  };

  const focusEditor = () => {
    editor.current.focus();
  };

  const handleReturn = (e, editorState) => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const currentBlock = contentState.getBlockForKey(startKey);
    if (currentBlock) {
      const blockData = currentBlock.getData();
      // const blockStyles = blockData.get('style');
      if (blockData) {
        // Original split logic
        const newContentState = Modifier.splitBlock(
          editorState.getCurrentContent(),
          editorState.getSelection(),
        );
        let splitState = EditorState.push(editorState, newContentState, 'split-block');
        const target = splitState.getSelection();
        const afterMergeStylesContentState = Modifier.mergeBlockData(
          splitState.getCurrentContent(),
          target,
          blockData,
        );
        splitState = EditorState.push(editorState, afterMergeStylesContentState, 'split-block');
        onChange(splitState);
        return 'handled';
      }
    }
    return 'not-handled';
  };

  const handleKeyCommand = command => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      onChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const myKeyBindingFn = e => {
    if (e.ctrlKey && (e.keyCode === 89 || e.keyCode === 90)) {
      return 'not-handled';
    }
    return getDefaultKeyBinding(e);
  };

  const handlePastedText = (text, html, editorState) => {
    const selection = editorState.getSelection();
    if (selection.isCollapsed()) {
      let anchorOffset;
      const content = editorState.getCurrentContent();
      let selection = editorState.getSelection();
      anchorOffset = selection.anchorOffset;

      const inlineStyles = editorState.getCurrentInlineStyle();

      const newContent = Modifier.insertText(content, selection, text);

      let newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
      selection = newEditorState.getSelection();
      const updateSelection = new SelectionState({
        anchorKey: selection.anchorKey,
        anchorOffset: anchorOffset,
        focusKey: selection.anchorKey,
        focusOffset: selection.focusOffset,
      });
      newEditorState = EditorState.forceSelection(newEditorState, updateSelection);

      inlineStyles
        .toArray()
        .forEach(
          inlineStyle =>
            (newEditorState = RichUtils.toggleInlineStyle(newEditorState, inlineStyle)),
        );

      onChange(newEditorState);
      return true;
    } else {
      let anchorOffset;
      const content = editorState.getCurrentContent();
      let selection = editorState.getSelection();

      let inlineStyles = editorState.getCurrentInlineStyle();
      let newContent = Modifier.removeRange(content, selection, 'forward');
      let newEditorState = EditorState.push(editorState, newContent, 'remove-range');
      selection = newEditorState.getSelection();
      anchorOffset = selection.anchorOffset;

      if (newEditorState.getCurrentInlineStyle().size > 0)
        inlineStyles = newEditorState.getCurrentInlineStyle();

      newContent = Modifier.insertText(newContent, selection, text);

      newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
      selection = newEditorState.getSelection();
      const updateSelection = new SelectionState({
        anchorKey: selection.anchorKey,
        anchorOffset: anchorOffset,
        focusKey: selection.anchorKey,
        focusOffset: selection.focusOffset,
      });
      newEditorState = EditorState.forceSelection(newEditorState, updateSelection);

      inlineStyles
        .toArray()
        .forEach(
          inlineStyle =>
            (newEditorState = RichUtils.toggleInlineStyle(newEditorState, inlineStyle)),
        );

      onChange(newEditorState);
      return true;
    }
  };

  const onChange = newEditorState => {
    dispatch(designActions.changeEditorState({ [props.entityId]: newEditorState }));
  };

  const onBlur = () => {
    editor.current.blur();
  };

  const {
    'transform-origin': legacyTransformOrigin,
    ...masterStyle
  } = _.get(entity, 'style.master', {});

  return editorState ? (
    <div
      id={`_${props.entityId}_`}
      data-lock={_.get(entity, 'lock')}
      data-page={props.pageId}
      data-type="Text"
      className={props.className}
      data-target={props.dataTarget}
      style={{
        ...masterStyle,
        transformOrigin: masterStyle.transformOrigin || legacyTransformOrigin,
        height: 'auto',
      }}
      onMouseDown={props.onMouseDown}
      onMouseOver={props.onMouseOver}
    >
      <div
        id={`ET_${props.entityId}`}
        className="textEditorHolder"
        onClick={focusEditor}
        onBlur={onBlur}
      >
        <Editor
          key={props.entityId}
          ref={editor}
          readOnly={props.entityId !== enabledEditorId}
          blockStyleFn={getBlockStyleFn}
          customStyleMap={getCustomStyleMap()}
          handleReturn={handleReturn}
          handleKeyCommand={handleKeyCommand}
          handlePastedText={handlePastedText}
          keyBindingFn={myKeyBindingFn}
          editorState={editorState}
          onChange={onChange}
        />
      </div>
    </div>
  ) : null;
};

VTextEditor.propTypes = {
  entityId: PropTypes.number,
  pageId: PropTypes.number,
  className: PropTypes.string,
  dataTarget: PropTypes.string,
  onMouseDown: PropTypes.func,
  onMouseOver: PropTypes.func,
};

export default React.memo(VTextEditor);
